import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HelpText } from 'src/components/atoms';
import { FormInput } from 'src/components/molecules';
import { SpinnerButton } from 'src/components/molecules/SpinnerButton';

import { FormInputVariant, FormState } from 'src/types';

import '../../../css/login/Login.css';
import {
  ButtonWrapper,
  FormContainer,
  PasswordHelpText,
} from './ChangePasswordNew.styles';
import { newPasswordRules } from './passwordRules';

interface Props {
  createPassword: (data: FormValues) => void;
  updateWithToken?: boolean;
  'data-testid'?: string;
  isLoading?: boolean;
  buttonText?: string;
  isLocalised?: boolean;
}

export interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

export const ChangePasswordNewCommon = ({
  createPassword,
  updateWithToken,
  isLoading,
  buttonText,
  isLocalised,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { isDirty, isValid, errors },
  } = useForm<FormValues>({
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const newPassword = watch('newPassword');
  const confirmNewPassword = watch('confirmNewPassword');

  const newPasswordState = !newPassword
    ? FormState.DEFAULT
    : errors.newPassword
      ? FormState.ERROR
      : FormState.SUCCESS;

  const confirmNewPasswordState = !confirmNewPassword
    ? FormState.DEFAULT
    : errors.confirmNewPassword
      ? FormState.ERROR
      : FormState.SUCCESS;

  const newPasswordLabel = !updateWithToken
    ? isLocalised
      ? t('reporting.ReportingLogin.newPasswordPlaceholder')
      : 'New password'
    : isLocalised
      ? t('reporting.ReportingLogin.passwordPlaceholder')
      : 'Password';

  return (
    <form onSubmit={handleSubmit(createPassword)}>
      <FormContainer>
        {!updateWithToken && (
          <FormInput
            label={t('reporting.ReportingLogin.currentPassword')}
            variant={FormInputVariant.INPUT}
            placeholder={t('reporting.ReportingLogin.currentPassword')}
            autoComplete="password"
            data-testid={`current-password-${props['data-testid']}`}
            state={FormState.DEFAULT}
            type="password"
            $removeMarginBottom={true}
            {...register('currentPassword', {
              required: true,
              onChange: () => trigger('confirmNewPassword'),
            })}
          />
        )}

        <div>
          <FormInput
            label={newPasswordLabel}
            variant={FormInputVariant.INPUT}
            placeholder={
              isLocalised
                ? t('reporting.ReportingLogin.passwordPlaceholder')
                : 'Password'
            }
            autoComplete="new-password"
            data-testid={`create-password-${props['data-testid']}`}
            state={newPasswordState}
            type="password"
            $removeMarginBottom={true}
            {...register('newPassword', {
              required: true,
              onChange: () => trigger('confirmNewPassword'),
              validate: newPasswordRules.reduce<
                Record<string, (value: string) => boolean>
              >(
                (acc, { key, validate }) => ({
                  ...acc,
                  [key]: validate,
                }),
                {},
              ),
            })}
          />

          <PasswordHelpText $showHelperText={newPassword?.length > 3}>
            {newPasswordRules.map(
              ({ key, message, translatedMessage }, index) => (
                <HelpText
                  key={index}
                  state={
                    errors.newPassword?.types?.[key]
                      ? FormState.ERROR
                      : FormState.SUCCESS
                  }
                  helpText={isLocalised ? translatedMessage() : message}
                  data-testid={key}
                  textSize="medium"
                  $margin="0"
                />
              ),
            )}
          </PasswordHelpText>
        </div>

        <div>
          <FormInput
            label={
              isLocalised
                ? t('reporting.ReportingLogin.reenterPlaceholder')
                : 'Re-enter password'
            }
            variant={FormInputVariant.INPUT}
            placeholder={
              isLocalised
                ? t('reporting.ReportingLogin.passwordPlaceholder')
                : 'Password'
            }
            autoComplete="new-password"
            data-testid={`confirm-password-${props['data-testid']}`}
            state={confirmNewPasswordState}
            type="password"
            $removeMarginBottom={true}
            {...register('confirmNewPassword', {
              required: true,
              validate: (value, data) => value === data.newPassword,
            })}
          />
          <PasswordHelpText
            $showHelperText={confirmNewPasswordState === FormState.ERROR}
          >
            <HelpText
              state={FormState.ERROR}
              data-testid="passwords-mismatch"
              helpText={
                isLocalised
                  ? t('validatorsNew.passwordMissMatch')
                  : 'Passwords don’t match'
              }
              textSize="medium"
              $margin="0"
            />
          </PasswordHelpText>
        </div>
        <ButtonWrapper>
          <SpinnerButton
            type="submit"
            variant="primary"
            size="medium"
            data-testid={props['data-testid'] || ''}
            disabled={!isDirty || !isValid}
            width="100%"
            loading={isLoading}
          >
            {buttonText ?? 'Update password'}
          </SpinnerButton>
        </ButtonWrapper>
      </FormContainer>
    </form>
  );
};
